import { Platform } from './platform'

/**
 * Platform Budget Support Configuration
 * Defines which budget features are supported by each advertising platform
 */
export interface PlatformBudgetCapabilities {
    /** Whether the platform supports shared budgets across campaigns */
    sharedBudget: boolean
    /** Whether campaign-level budgets can be edited */
    campaignBudget: boolean
    /** Whether ad group/ad set-level budgets can be edited */
    adGroupBudget: boolean
    /** Supported budget types for this platform */
    budgetTypes: ReadonlyArray<'DAILY' | 'LIFETIME'>
}

/**
 * Single source of truth for platform budget capabilities
 * Used across frontend and backend to determine what budget operations are allowed
 */
export const PLATFORM_BUDGET_SUPPORT: Record<Platform, PlatformBudgetCapabilities> = {
    [Platform.GoogleAds]: {
        sharedBudget: true,
        campaignBudget: true,
        adGroupBudget: false,
        budgetTypes: ['DAILY'] as const,
    },
    [Platform.MicrosoftAds]: {
        sharedBudget: true,
        campaignBudget: true,
        adGroupBudget: false,
        budgetTypes: ['DAILY'] as const,
    },
    [Platform.MetaAds]: {
        sharedBudget: false,
        campaignBudget: true,
        adGroupBudget: true,
        budgetTypes: ['DAILY', 'LIFETIME'] as const,
    },
    [Platform.LinkedInAds]: {
        sharedBudget: false,
        campaignBudget: true,
        adGroupBudget: false,
        budgetTypes: ['DAILY'] as const,
    },
    [Platform.GoogleAnalytics]: {
        // Google Analytics doesn't manage ad budgets - it's an analytics platform
        sharedBudget: false,
        campaignBudget: false,
        adGroupBudget: false,
        budgetTypes: [] as const,
    },
    [Platform.GoogleSearchConsole]: {
        // Google Search Console doesn't manage ad budgets - it's a search analytics platform
        sharedBudget: false,
        campaignBudget: false,
        adGroupBudget: false,
        budgetTypes: [] as const,
    },
    [Platform.TikTokAds]: {
        sharedBudget: false,
        campaignBudget: true,
        // Ad group budgets are supported by TikTok but not yet implemented in the backend
        adGroupBudget: false,
        budgetTypes: ['DAILY', 'LIFETIME'] as const,
    },
    [Platform.ChatGptAds]: {
        // Campaign-level only, no shared budgets. Ad groups have no budget of their own in the
        // Ads API - they carry a bid config instead (see updatePlatformCampaignBiddingStrategy).
        sharedBudget: false,
        campaignBudget: true,
        adGroupBudget: false,
        budgetTypes: ['DAILY', 'LIFETIME'] as const,
    },
} as const

/**
 * Helper function to check if a platform supports shared budgets
 */
export function platformSupportsSharedBudgets(platform: Platform): boolean {
    return PLATFORM_BUDGET_SUPPORT[platform]?.sharedBudget ?? false
}

/**
 * Helper function to check if a platform supports campaign budgets
 */
export function platformSupportsCampaignBudgets(platform: Platform): boolean {
    return PLATFORM_BUDGET_SUPPORT[platform]?.campaignBudget ?? false
}

/**
 * Helper function to check if a platform supports ad group budgets
 */
export function platformSupportsAdGroupBudgets(platform: Platform): boolean {
    return PLATFORM_BUDGET_SUPPORT[platform]?.adGroupBudget ?? false
}

/**
 * Helper function to check if a platform supports a specific budget type
 */
export function platformSupportsBudgetType(
    platform: Platform,
    budgetType: 'DAILY' | 'LIFETIME'
): boolean {
    return PLATFORM_BUDGET_SUPPORT[platform]?.budgetTypes.includes(budgetType) ?? false
}

/**
 * Minimum daily budget allowed per platform (in USD)
 * These values are converted to account currency at runtime using exchange rates
 */
export const PLATFORM_MIN_DAILY_BUDGET_USD: Record<Platform, number> = {
    [Platform.GoogleAds]: 0.01,
    [Platform.MicrosoftAds]: 0.05,
    [Platform.MetaAds]: 1,
    [Platform.LinkedInAds]: 10,
    [Platform.GoogleAnalytics]: 0,
    [Platform.GoogleSearchConsole]: 0,
    [Platform.TikTokAds]: 1,
    // Unconfirmed - the Ads API's minimum campaign budget isn't documented publicly yet.
    [Platform.ChatGptAds]: 0,
}

/**
 * Get the minimum daily budget for a platform in USD
 */
export function getMinDailyBudgetUSD(platform: Platform): number {
    return PLATFORM_MIN_DAILY_BUDGET_USD[platform] ?? 0
}
